import type { Feature } from './feature'
import type { DocumentKey } from './models'

/** Exposes upload, download, and delete status for attribute documents. */
export class Selectors {
  /** Whether a document is currently uploading. */
  public readonly isUploading
  /** Failure from the current upload request. */
  public readonly uploadError

  /** Whether a document's download URL is currently being requested. */
  public readonly isDownloading
  /** Temporary URL resolved for a document, once available. */
  public readonly downloadUrl
  /** Failure from the current download request. */
  public readonly downloadError

  /** Whether a document is currently being deleted. */
  public readonly isDeleting
  /** Failure from the current delete request. */
  public readonly deleteError
  /** Whether the most recently completed delete request succeeded. */
  public readonly deleteSuccess

  /** Files staged for upload but not yet submitted. */
  public readonly selection

  constructor(feature: Feature) {
    const uploads = feature.state.project(state => state.uploads)
    const downloads = feature.state.project(state => state.downloads)
    const deletions = feature.state.project(state => state.deletions)
    const selections = feature.state.project(state => state.selections)

    const uploadEntry = feature.createSelectorMethod(
      (key: DocumentKey) => uploads.project(lookup => lookup[key]).asSelector,
    )
    const downloadEntry = feature.createSelectorMethod(
      (key: DocumentKey) => downloads.project(lookup => lookup[key]).asSelector,
    )
    const deletionEntry = feature.createSelectorMethod(
      (key: DocumentKey) => deletions.project(lookup => lookup[key]).asSelector,
    )

    this.isUploading = feature.createSelectorMethod(
      (key: DocumentKey) =>
        uploadEntry(key).project(entry => entry?.loading === true).asSelector,
    )
    this.uploadError = feature.createSelectorMethod(
      (key: DocumentKey) =>
        uploadEntry(key).project(entry => entry?.error).asSelector,
    )

    this.isDownloading = feature.createSelectorMethod(
      (key: DocumentKey) =>
        downloadEntry(key).project(entry => entry?.loading === true).asSelector,
    )
    this.downloadUrl = feature.createSelectorMethod(
      (key: DocumentKey) =>
        downloadEntry(key).project(entry => entry?.data).asSelector,
    )
    this.downloadError = feature.createSelectorMethod(
      (key: DocumentKey) =>
        downloadEntry(key).project(entry => entry?.error).asSelector,
    )

    this.isDeleting = feature.createSelectorMethod(
      (key: DocumentKey) =>
        deletionEntry(key).project(entry => entry?.loading === true).asSelector,
    )
    this.deleteError = feature.createSelectorMethod(
      (key: DocumentKey) =>
        deletionEntry(key).project(entry => entry?.error).asSelector,
    )
    this.deleteSuccess = feature.createSelectorMethod(
      (key: DocumentKey) =>
        deletionEntry(key).project(entry => entry?.data === true).asSelector,
    )

    this.selection = feature.createSelectorMethod(
      (key: DocumentKey) =>
        selections.project(lookup => lookup[key]).asSelector,
    )
  }
}
